import React, { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Search, Trash2, Check, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const GRADES = [
  { k: "all", label: "الكل" },
  { k: "1", label: "أول ثانوي" },
  { k: "2", label: "ثاني ثانوي" },
  { k: "3", label: "ثالث ثانوي" },
  { k: "other", label: "أخرى" },
];

// توحيد اسم الصف لأي صيغة مكتوبة (أول/الأول/اول/1...)
export const gradeKey = (g) => {
  const s = (g || "").replace(/[أإآ]/g, "ا").replace(/[١]/g, "1").replace(/[٢]/g, "2").replace(/[٣]/g, "3");
  if (/ثالث|ثالثه|3/.test(s)) return "3";
  if (/ثاني|ثانيه|2/.test(s)) return "2";
  if (/اول|اولى|1/.test(s)) return "1";
  return "other";
};

const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

// كشف مشاركي الفرصة: إضافة طلاب من قائمة المدرسة + اعتماد المنجزين + الساعات الفعلية
export const OpportunityParticipants = ({ opp, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [roster, setRoster] = useState([]);
  const [q, setQ] = useState("");
  const [grade, setGrade] = useState("all");
  const [picked, setPicked] = useState(new Set());
  const [adding, setAdding] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const load = () => {
    api.get(`/opportunities/${opp.id}/participants`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => {
    load();
    const qp = opp.school_id ? `?school_id=${opp.school_id}` : "";
    api.get(`/volunteers${qp}`).then((r) => setRoster(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [opp.id]);

  const enrolledIds = useMemo(() => new Set((data?.participants || []).map((p) => p.volunteer_id)), [data]);
  const nq = q.trim();
  const notEnrolled = useMemo(
    () => roster.filter((v) => !enrolledIds.has(v.id))
      .map((v) => ({ ...v, gk: gradeKey(v.grade || v.job_title) }))
      .sort((a, b) => a.gk.localeCompare(b.gk) || (a.name || "").localeCompare(b.name || "", "ar")),
    [roster, enrolledIds]
  );
  const gradeCounts = useMemo(() => {
    const c = { all: notEnrolled.length, 1: 0, 2: 0, 3: 0, other: 0 };
    notEnrolled.forEach((v) => { c[v.gk] += 1; });
    return c;
  }, [notEnrolled]);
  const available = useMemo(
    () => notEnrolled.filter((v) => (grade === "all" || v.gk === grade) && (!nq || (v.name || "").includes(nq) || (v.grade || "").includes(nq) || (v.job_title || "").includes(nq))),
    [notEnrolled, grade, nq]
  );

  const seatsLeft = data ? (data.seats > 0 ? data.seats - data.count : Infinity) : 0;

  const toggle = (id) => setPicked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else if (n.size >= seatsLeft) { toast.error(`بلغت الحد المتبقي للمقاعد (${seatsLeft})`); return s; }
    else n.add(id);
    return n;
  });
  const allShownPicked = available.length > 0 && available.every((v) => picked.has(v.id));
  const toggleAllShown = () => setPicked((s) => {
    const n = new Set(s);
    if (allShownPicked) { available.forEach((v) => n.delete(v.id)); return n; }
    for (const v of available) {
      if (n.size >= seatsLeft) { toast.warning(`تم تحديد ${n.size} — الحد المتبقي للمقاعد ${seatsLeft}`); break; }
      n.add(v.id);
    }
    return n;
  });

  const addPicked = async () => {
    if (!picked.size) return;
    setAdding(true);
    try {
      const res = await api.post(`/opportunities/${opp.id}/participants`, { volunteer_ids: [...picked] });
      playSuccess();
      let msg = `تمت إضافة ${res.data.added} مشارك`;
      if (res.data.skipped) msg += ` (تخطي ${res.data.skipped})`;
      if (res.data.full) msg += " — اكتمل الحد الأقصى للمقاعد";
      toast.success(msg);
      setPicked(new Set()); setShowPicker(false); setQ(""); setGrade("all");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setAdding(false); }
  };

  const setDone = async (p, completed) => {
    try {
      await api.put(`/opportunities/${opp.id}/participants/${p.id}`, { completed });
      load(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const setHours = async (p, hours) => {
    const h = Math.max(0, +hours || 0);
    try {
      await api.put(`/opportunities/${opp.id}/participants/${p.id}`, { hours: h });
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/opportunities/${opp.id}/participants/${p.id}`); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const completedCount = (data?.participants || []).filter((p) => p.completed).length;
  const totalHours = (data?.participants || []).filter((p) => p.completed).reduce((s, p) => s + (p.hours || 0), 0);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle className="gold-text font-head flex items-center gap-2"><Users size={18} /> كشف المشاركين — {opp.name}</DialogTitle></DialogHeader>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-3 py-1 rounded-full bg-sky-500/15 text-sky-700" data-testid="part-count">المسجّلون: {data?.count ?? 0}{data?.seats ? ` / ${data.seats}` : ""}</span>
          <span className="px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-700" data-testid="part-completed">منجزون: {completedCount}</span>
          <span className="px-3 py-1 rounded-full bg-amber-500/15 text-amber-700" data-testid="part-hours">الساعات الفعلية: {Math.round(totalHours * 10) / 10}</span>
          <span className="px-3 py-1 rounded-full bg-stone-500/15 text-stone-700">ساعات الفرصة: {data?.credit_hours ?? 0}</span>
        </div>

        {!showPicker ? (
          <button data-testid="open-add-participants" onClick={() => setShowPicker(true)} disabled={data && data.seats > 0 && data.count >= data.seats}
            className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2 w-fit disabled:opacity-50 mt-1">
            <Plus size={16} /> {data && data.seats > 0 && data.count >= data.seats ? "اكتملت المقاعد" : "إضافة طلاب"}
          </button>
        ) : (
          <div className="rounded-xl border border-amber-400/30 bg-amber-50/60 p-3 mt-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-stone-700">اختر الطلاب المشاركين {Number.isFinite(seatsLeft) ? `(المتبقّي: ${Math.max(0, seatsLeft)})` : ""}</span>
              <button data-testid="close-add-participants" onClick={() => { setShowPicker(false); setPicked(new Set()); setQ(""); setGrade("all"); }} className="text-stone-500 hover:text-stone-800"><X size={18} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="grade-filter">
              {GRADES.filter((g) => g.k !== "other" || gradeCounts.other > 0).map((g) => (
                <button key={g.k} type="button" data-testid={`grade-tab-${g.k}`} onClick={() => setGrade(g.k)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${grade === g.k ? "bg-amber-600 text-white border-amber-600" : "bg-white text-stone-700 border-amber-400/40 hover:bg-amber-100"}`}>
                  {g.label} ({gradeCounts[g.k]})
                </button>
              ))}
            </div>
            <div className="relative mb-2">
              <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input data-testid="participant-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن طالب بالاسم أو الصف..." className={`${inputCls} pr-9`} />
            </div>
            <div className="flex items-center justify-between mb-1 text-xs text-stone-600">
              <button type="button" data-testid="select-all-shown" onClick={toggleAllShown} disabled={!available.length}
                className="px-3 py-1 rounded-lg border border-emerald-500/40 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
                {allShownPicked ? "إلغاء تحديد المعروض" : `تحديد الكل المعروض (${available.length})`}
              </button>
              <span data-testid="picked-count">المحدد: {picked.size}</span>
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {available.map((v) => {
                const on = picked.has(v.id);
                return (
                  <button key={v.id} type="button" data-testid={`pick-${v.id}`} onClick={() => toggle(v.id)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm text-right transition ${on ? "bg-emerald-500/15 border-emerald-400/50" : "bg-white border-amber-400/20 hover:bg-amber-100/50"}`}>
                    <span className="truncate">{v.name} <span className="text-[10px] text-stone-400">{v.grade || v.job_title || ""}</span></span>
                    <span className={`w-5 h-5 rounded flex items-center justify-center ${on ? "bg-emerald-600 text-white" : "border border-stone-300"}`}>{on && <Check size={13} />}</span>
                  </button>
                );
              })}
              {!available.length && <div className="text-center text-stone-500 text-sm py-4">لا يوجد طلاب مطابقون. أضف الطلاب من صفحة «الطلاب/المتطوعون» أولاً.</div>}
            </div>
            <button data-testid="confirm-add-participants" onClick={addPicked} disabled={adding || !picked.size}
              className="btn-gold px-5 py-2 rounded-xl mt-2 disabled:opacity-50">{adding ? "جارٍ الإضافة..." : `إضافة المحدد (${picked.size})`}</button>
          </div>
        )}

        <div className="mt-2">
          {!data?.participants?.length ? (
            <div className="text-center text-stone-500 text-sm py-6">لا يوجد مشاركون بعد. اضغط «إضافة طلاب» لتسجيل المشاركين واحتساب ساعاتهم الفعلية.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-stone-500 text-right border-b border-amber-400/15">
                  <th className="py-2 px-2 font-medium">الطالب</th><th className="py-2 px-2 font-medium">الصف</th>
                  <th className="py-2 px-2 font-medium">منجز</th><th className="py-2 px-2 font-medium">الساعات</th><th className="py-2 px-2 font-medium"></th>
                </tr></thead>
                <tbody>
                  {data.participants.map((p) => (
                    <tr key={p.id} data-testid={`participant-row-${p.volunteer_id}`} className="border-b border-amber-400/10">
                      <td className="py-2 px-2 text-stone-800 font-medium">{p.name}</td>
                      <td className="py-2 px-2 text-stone-500 text-xs">{p.grade}</td>
                      <td className="py-2 px-2">
                        <button data-testid={`toggle-done-${p.volunteer_id}`} onClick={() => setDone(p, !p.completed)}
                          className={`px-2 py-1 rounded-lg text-xs flex items-center gap-1 ${p.completed ? "bg-emerald-600 text-white" : "bg-stone-200 text-stone-600"}`}>
                          {p.completed ? <><Check size={12} /> منجز</> : "لم يُنجز"}
                        </button>
                      </td>
                      <td className="py-2 px-2">
                        <input data-testid={`hours-${p.volunteer_id}`} type="number" min="0" max="50" defaultValue={p.hours}
                          onBlur={(e) => setHours(p, e.target.value)} className="w-20 px-2 py-1 rounded-lg bg-amber-50 border border-amber-400/30 text-sm" />
                      </td>
                      <td className="py-2 px-2">
                        <button data-testid={`remove-participant-${p.volunteer_id}`} onClick={() => remove(p)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-700"><Trash2 size={15} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OpportunityParticipants;
